import { Navbar, NavbarBrand, NavbarContent, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, User, addToast, Button } from "@heroui/react";
import { IconHome, IconLogout, IconPlus, IconSchool, IconSettings, IconUser } from "@tabler/icons-react";
import { getSession, signOut, useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";
import { apiFetch } from "@/lib/api";
import { Suspense, useEffect, useState } from "react";
import ProfileModal from "./ProfileModal";
import CompleteRegistrationModal from "./CompleteRegistrationModal";
import NewProjectModal from "./NewProjectModal";

interface NavBarProps {
  className?: string;
  setIsLogoutLoading?: (value: boolean) => void;
  hasLogoutLoading: boolean;
}

export default function NavBar({ className, hasLogoutLoading, setIsLogoutLoading }: NavBarProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const pathname = usePathname();
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    if (!session?.accessToken) return;

    const freshSession = await getSession();

    if (hasLogoutLoading && setIsLogoutLoading) {
      setIsLogoutLoading(true);
    }

    const res = await apiFetch("/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.accessToken}` },
      body: JSON.stringify({
        access_token: freshSession?.refreshToken, // 👈 sempre o RT mais novo
      }),
    });

    if (res.ok) {
      await signOut({
        redirect: true,
        callbackUrl: "/login",
      });
      router.refresh();
      if (hasLogoutLoading && setIsLogoutLoading) {
        setIsLogoutLoading(true);
      }
    } else {
      await signOut({
        redirect: true,
        callbackUrl: "/login",
      });
      addToast({
        title: "Erro ao fazer logout",
        description: "Você foi deslogado localmente, mas houve um problema ao deslogar no servidor",
        color: "danger",
      });
      router.refresh();
      if (hasLogoutLoading && setIsLogoutLoading) {
        setIsLogoutLoading(true);
      }
    }
  };

  useEffect(() => {
    if (!session?.user.logOut) return;

    handleLogout();
  }, []);

  const handleModalOpen = () => {
    router.push(`${pathname}?modal=editProfile`);
  };

  const handleOpenNewProjectModal = () => {
    router.push(`${pathname}?modal=newProject`);
  };

  return (
    <>
      <Suspense fallback={null}>
        <ProfileModal />
        <CompleteRegistrationModal />
        <NewProjectModal />
      </Suspense>
      <Navbar
        className={`${className} max-w-screen backdrop-blur-md shadow-sm transition-all duration-300 ${isScrolling ? "bg-transparent" : "nav-soft-bg"}`}
      >
        <button className="cursor-pointer" onClick={() => router.push("/")}>
          <NavbarBrand className="gap-1">
            <Image src="/logo.png" quality={100} height={30} width={30} alt="Univc logo" />
            <p className="text-black">Univc e-Portifolio</p>
          </NavbarBrand>
        </button>
        <NavbarContent justify="end">
          {(session?.user.cargo === "ADMIN" || session?.user.cargo === "PROFESSOR") && (
            <>
              <Button
                color="success"
                className="max-md:hidden"
                variant="flat"
                startContent={<IconPlus size={20} />}
                onPress={handleOpenNewProjectModal}
              >
                Novo Projeto
              </Button>
              <Button
                color="success"
                className="max-md:hidden"
                variant="flat"
                startContent={<IconSchool size={20} />}
                onPress={() => router.push("/professor")}
              >
                Professor
              </Button>
            </>
          )}
          <Dropdown>
            <DropdownTrigger>
              <User
                className="cursor-pointer"
                avatarProps={{
                  src: session?.user.image,
                }}
                description=""
                name=""
              />
            </DropdownTrigger>
            <DropdownMenu aria-label="Static Actions">
              <DropdownItem key="home" startContent={<IconHome size={20} />} onPress={() => router.push("/")}>
                Home
              </DropdownItem>
              {session?.user.cargo === "ADMIN" && (
                <>
                  <DropdownItem className="flex flex-row" key="admin" startContent={<IconSettings size={20} />} onPress={() => router.push("/admin")}>
                    Admin
                  </DropdownItem>
                  <DropdownItem className="md:hidden" key="teacher" startContent={<IconSchool size={20} />} onPress={() => router.push("/professor")}>
                    Professor
                  </DropdownItem>
                  <DropdownItem className="md:hidden" key="newProject" startContent={<IconPlus size={20} />} onPress={handleOpenNewProjectModal}>
                    Novo Projeto
                  </DropdownItem>
                </>
              )}
              {session?.user.cargo === "PROFESSOR" && (
                <>
                  <DropdownItem className="md:hidden" key="newProject" startContent={<IconPlus size={20} />} onPress={handleOpenNewProjectModal}>
                    Novo Projeto
                  </DropdownItem>
                  <DropdownItem className="md:hidden" key="teacher" startContent={<IconSchool size={20} />} onPress={() => router.push("/professor")}>
                    Professor
                  </DropdownItem>
                </>
              )}
              <DropdownItem onPress={handleModalOpen} startContent={<IconUser size={20} />} key="profile" className="text-black flex flex-row">
                Perfil
              </DropdownItem>
              <DropdownItem
                key="logout"
                onPress={handleLogout}
                startContent={<IconLogout size={20} />}
                className="text-danger flex flex-row"
                color="danger"
              >
                Sair
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarContent>
      </Navbar>
    </>
  );
}
